/**
 * ColumnsPicker — the "Columns" pop-over of the wide grids (Securities, Portfolios): show or
 * hide each column, and drag the list to re-order the columns (HP-V2, Pedro 2026-10-19).
 *
 * The columns are listed in their current order, each with a drag handle, a checkbox and its
 * group (e.g. Identity, a classification lens, Position) in grey. "Show all" clears the hidden
 * set; "Reset order" brings back the default order and widths. A column that cannot be shown
 * right now (e.g. a classification used for grouping in View portfolio) is greyed with the
 * reason (§3h: greyed with a reason, not hidden). When the columns carry an ORIGIN (View
 * portfolio, app-language 17), each row shows its grey origin icon and chips at the top narrow
 * the LIST to one or more origins (HP-V4, Pedro: "narrow the list"; the table is unchanged).
 * The page owns the hidden set, the order and the widths (remembered locally; the order
 * through `components/gridColumnOrder`). Extracted 2026-10-18 from SecuritiesPage so both
 * grids share one control (§3h).
 */
import { Anchor, Button, Checkbox, Chip, Group, Popover, ScrollArea, Stack, Text, Tooltip } from '@mantine/core';
import { IconColumns, IconGripVertical } from '@tabler/icons-react';
import { DragDropContext, Draggable, Droppable, type DropResult } from '@hello-pangea/dnd';
import { useState } from 'react';

import { ORIGIN_COLOR, ORIGIN_ORDER, ORIGINS, type Origin } from './columnOrigin';

export interface PickableColumn { key: string; label: string; group: string; origin?: Origin }

/** HP-C2: a second tick per measure, "per portfolio" (View portfolio with several portfolios). */
export interface SplitTicks {
  allowed: Set<string>;               // the columns that can be split by portfolio
  on: Set<string>;                    // ticked
  onToggle: (key: string) => void;
  disabledReason?: string;            // greyed with this reason (e.g. the switch is not on "Chosen")
}

export function ColumnsPicker({
  columns, hidden, onToggle, onShowAll, size, onPlace, onResetOrder, customOrder, unavailable, split,
}: {
  /** In their current order. */
  columns: PickableColumn[];
  hidden: Set<string>;
  onToggle: (key: string) => void;
  onShowAll: () => void;
  /** The trigger button's size (default 'sm'; the compact Portfolios toolbar uses 'xs'). */
  size?: 'xs' | 'sm';
  /** Put a column just before (or after) another. */
  onPlace: (key: string, target: string, after: boolean) => void;
  onResetOrder: () => void;
  /** True when the order or the widths differ from the default (shows "Reset order"). */
  customOrder: boolean;
  /** Columns that cannot be shown now → the reason, shown greyed. */
  unavailable?: Record<string, string>;
  split?: SplitTicks;
}) {
  const [open, setOpen] = useState(false);
  const present = ORIGIN_ORDER.filter((o) => columns.some((c) => c.origin === o));
  const [shownOrigins, setShownOrigins] = useState<string[]>(ORIGIN_ORDER);
  const listed = present.length
    ? columns.filter((c) => !c.origin || shownOrigins.includes(c.origin))
    : columns;
  const hiddenCount = columns.filter((c) => hidden.has(c.key)).length;
  // Indices are those of the (possibly narrowed) list: drop next to the row now at that place.
  const onDragEnd = (r: DropResult) => {
    if (!r.destination || r.destination.index === r.source.index) return;
    const target = listed[r.destination.index];
    if (target) onPlace(r.draggableId, target.key, r.destination.index > r.source.index);
  };
  return (
    <Popover width={330} position="bottom-end" withArrow shadow="md" opened={open} onChange={setOpen}>
      <Popover.Target>
        <Button variant="light" size={size} leftSection={<IconColumns size={16} />} onClick={() => setOpen((o) => !o)}>
          Columns{hiddenCount ? ` (${hiddenCount} hidden)` : ''}
        </Button>
      </Popover.Target>
      <Popover.Dropdown>
        <Stack gap="xs">
          <Group justify="space-between">
            <Text size="xs" c="dimmed" tt="uppercase">Show and order columns</Text>
            <Group gap="sm">
              {customOrder && <Anchor size="xs" onClick={onResetOrder}>Reset order</Anchor>}
              <Anchor size="xs" onClick={onShowAll}>Show all</Anchor>
            </Group>
          </Group>
          {present.length > 1 && (
            <Chip.Group multiple value={shownOrigins} onChange={setShownOrigins}>
              <Group gap={6}>
                {present.map((o) => {
                  const O = ORIGINS[o];
                  return (
                    <Chip key={o} value={o} size="xs" variant="outline" color="gray">
                      <Group gap={4} wrap="nowrap"><O.icon size={12} style={{ color: ORIGIN_COLOR }} />{O.short}</Group>
                    </Chip>
                  );
                })}
              </Group>
            </Chip.Group>
          )}
          <Text size="xs" c="dimmed">Drag a row, or a column heading in the table, to move a column.</Text>
          <ScrollArea.Autosize mah={420}>
            {listed.length === 0 && <Text size="xs" c="dimmed">No column of the chosen origins.</Text>}
            <DragDropContext onDragEnd={onDragEnd}>
              <Droppable droppableId="columns">
                {(drop) => (
                  <div ref={drop.innerRef} {...drop.droppableProps}>
                    {listed.map((c, i) => {
                      const reason = unavailable?.[c.key];
                      const O = c.origin ? ORIGINS[c.origin] : null;
                      return (
                        <Draggable key={c.key} draggableId={c.key} index={i}>
                          {(d) => (
                            <div ref={d.innerRef} {...d.draggableProps}
                              style={{ ...d.draggableProps.style, padding: '2px 0', background: 'var(--mantine-color-body)' }}>
                              <Group gap={6} wrap="nowrap">
                                <span {...d.dragHandleProps} style={{ display: 'inline-flex', cursor: 'grab', color: 'var(--mantine-color-gray-5)' }}
                                  aria-label={`Move ${c.label}`}>
                                  <IconGripVertical size={14} />
                                </span>
                                {O && (
                                  <Tooltip label={O.text} withArrow position="left">
                                    <span style={{ display: 'inline-flex' }}><O.icon size={13} style={{ color: ORIGIN_COLOR }} /></span>
                                  </Tooltip>
                                )}
                                <Tooltip label={reason} disabled={!reason} withArrow position="left">
                                  <span style={{ flex: 1, minWidth: 0 }}>
                                    <Checkbox size="xs" disabled={!!reason}
                                      label={<>{c.label} <Text span size="xs" c="dimmed">{reason ? `· ${reason}` : `· ${c.group}`}</Text></>}
                                      checked={!reason && !hidden.has(c.key)} onChange={() => onToggle(c.key)} />
                                  </span>
                                </Tooltip>
                                {split && split.allowed.has(c.key) && (
                                  <Tooltip withArrow position="left" multiline w={240}
                                    label={split.disabledReason ?? 'Ticked: a column for each portfolio as well as the Total. Unticked: the Total only.'}>
                                    <span style={{ flex: '0 0 auto' }}>
                                      <Checkbox size="xs" label="per portfolio" disabled={!!split.disabledReason || !!reason}
                                        checked={split.on.has(c.key)} onChange={() => split.onToggle(c.key)} />
                                    </span>
                                  </Tooltip>
                                )}
                              </Group>
                            </div>
                          )}
                        </Draggable>
                      );
                    })}
                    {drop.placeholder}
                  </div>
                )}
              </Droppable>
            </DragDropContext>
          </ScrollArea.Autosize>
        </Stack>
      </Popover.Dropdown>
    </Popover>
  );
}
